import type { TicketFormRule } from './ticket-form-api';

export type TicketFormRuleMode = 'agent' | 'customer';

export interface EvaluatedTicketFormFieldState {
  fieldKey: string;
  visible: boolean;
  required: boolean;
  readonly: boolean;
}

function isEmpty(value: unknown): boolean {
  return value === undefined
    || value === null
    || (typeof value === 'string' && value.trim() === '')
    || (Array.isArray(value) && value.length === 0);
}

function sameValue(left: unknown, right: unknown): boolean {
  if (typeof left === 'string' || typeof right === 'string') return String(left) === String(right);
  return Object.is(left, right);
}

function conditionMatches(condition: TicketFormRule['conditions'][number], values: Record<string, unknown>): boolean {
  const actual = values[condition.fieldKey];
  if (condition.operator === 'empty') {
    return isEmpty(actual) === (condition.value === undefined ? true : Boolean(condition.value));
  }
  if (condition.operator === 'in') {
    const candidates = Array.isArray(condition.value) ? condition.value : null;
    if (!candidates) return false;
    return Array.isArray(actual)
      ? actual.some((entry) => candidates.some((candidate) => sameValue(entry, candidate)))
      : candidates.some((candidate) => sameValue(actual, candidate));
  }
  return Array.isArray(actual)
    ? actual.some((entry) => sameValue(entry, condition.value))
    : sameValue(actual, condition.value);
}

function ruleMatches(rule: TicketFormRule, values: Record<string, unknown>): boolean {
  const results = rule.conditions.map((condition) => conditionMatches(condition, values));
  return rule.logic === 'OR' ? results.some(Boolean) : results.every(Boolean);
}

export function evaluateTicketFormRules(
  fields: readonly {
    fieldKey: string;
    enabled: boolean;
    visibleToCustomers: boolean;
    displayedToCustomers?: boolean;
    requiredForAgents?: boolean;
    requiredForCustomers?: boolean;
    customersCanEdit?: boolean;
    requiredOverride?: boolean | null;
    sourceType?: string | null;
  }[],
  rules: readonly TicketFormRule[],
  inputValues: Record<string, unknown>,
  mode: TicketFormRuleMode,
): { values: Record<string, unknown>; fields: EvaluatedTicketFormFieldState[] } {
  const values = { ...inputValues };
  const customerVisible = (field: (typeof fields)[number]) => field.visibleToCustomers
    && field.displayedToCustomers !== false
    && field.sourceType !== 'default_internal_group'
    && field.sourceType !== 'default_internal_agent';
  const states = new Map<string, EvaluatedTicketFormFieldState>(fields.map((field) => [field.fieldKey, {
    fieldKey: field.fieldKey,
    visible: field.enabled && (mode === 'agent' || customerVisible(field)),
    required: field.requiredOverride ?? (mode === 'customer' ? field.requiredForCustomers === true : field.requiredForAgents === true),
    readonly: mode === 'customer' && field.customersCanEdit === false,
  }]));

  for (const rule of [...rules]
    .filter((candidate) => candidate.isActive !== false)
    .sort((left, right) => left.position - right.position)) {
    if (!rule.conditions.length || !ruleMatches(rule, values)) continue;
    const state = states.get(rule.targetFieldKey);
    if (!state) continue;
    if (rule.action === 'show') {
      const targetField = fields.find((field) => field.fieldKey === rule.targetFieldKey);
      state.visible = targetField?.enabled !== false && (mode === 'agent' || customerVisible(targetField!));
    }
    if (rule.action === 'hide') state.visible = false;
    if (rule.action === 'required') state.required = true;
    if (rule.action === 'optional') state.required = false;
    if (rule.action === 'readonly') state.readonly = true;
    if (rule.action === 'value') values[rule.targetFieldKey] = rule.actionValue;
  }

  return { values, fields: [...states.values()] };
}
